import { ReactNode, useMemo } from 'react';

import { CtaType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';

import cx from 'classnames';

import { CtaClickedEvent, trackCtaClicked, TrackingProductLocation } from '~/util/trackingHelper';

import CachedImage from '../../images/CachedImage';

import styles from './MarketingProductCard.module.less';

export enum ColorFamily {
  Blue,
  Orange,
  BlueRefresh,
  Forest,
}

type MPCardSharedProps = {
  altText?: string;
  buttonText: string;
  bodyText: string;
  children?: ReactNode;
  className?: string;
  header: string;
  image?: string;
  imageWidth: number;
  url: string;
  /** for background colors */
  colorFamily?: ColorFamily;
  /** for event tracking */
  location?: TrackingProductLocation;
  /** for event tracking */
  position?: number;
  /** for event tracking */
  section?: string;
  /** for event tracking */
  businessUnit?: string;
  /** optional all-in-one tracking payload */
  tracking?: Partial<CtaClickedEvent>;
};

interface CardWithImageProps extends MPCardSharedProps {
  children?: never;
  image: string;
}

interface CardWithChildrenProps extends MPCardSharedProps {
  children: ReactNode;
  image?: never;
}

export const MarketingProductCard = ({
  altText,
  buttonText,
  bodyText,
  children,
  className,
  colorFamily = ColorFamily.Blue,
  header,
  image,
  imageWidth,
  location,
  position,
  section,
  url,
  businessUnit,
  tracking,
}: CardWithImageProps | CardWithChildrenProps): JSX.Element => {
  const cta = {
    ctaId: buttonText,
    cta: url,
    location,
    ctaType: CtaType.BUTTON,
    position,
    section,
    business_unit: businessUnit,
    ...tracking,
  };

  const colorway = useMemo(() => {
    switch (colorFamily) {
      case ColorFamily.BlueRefresh:
        return 'sky';
      case ColorFamily.Forest:
        return 'mint';
      default:
        return 'black';
    }
  }, [colorFamily]);

  return (
    <div className={cx(styles.marketingProductCardContainer, className && styles[className])}>
      <div
        className={cx(styles.imageContainer, {
          [styles.imageContainerBlue]: colorFamily === ColorFamily.Blue,
          [styles.imageContainerOrange]: colorFamily === ColorFamily.Orange,
          [styles.imageContainerBlueRefresh]: colorFamily === ColorFamily.BlueRefresh,
          [styles.imageContainerForest]: colorFamily === ColorFamily.Forest,
        })}
      >
        {children || (
          <CachedImage
            alt={altText}
            src={image}
            className={styles.vendorCoverImage}
            width={imageWidth}
            fit="crop"
          />
        )}
      </div>
      <div
        className={cx(styles.textContainer, {
          [styles.textContainerBlue]: colorFamily === ColorFamily.Blue,
          [styles.textContainerOrange]: colorFamily === ColorFamily.Orange,
          [styles.textContainerBlueRefresh]: colorFamily === ColorFamily.BlueRefresh,
          [styles.textContainerForest]: colorFamily === ColorFamily.Forest,
        })}
        data-testid="marketing-product-card"
      >
        <h3 className={styles.header}>{header}</h3>
        <p className={styles.bodyText}>{bodyText}</p>
        <ButtonV3
          component="a"
          variant="primary"
          colorway={colorway}
          href={url}
          onClick={() => trackCtaClicked(cta)}
          fullWidth={false}
        >
          {buttonText}
        </ButtonV3>
      </div>
    </div>
  );
};

export default MarketingProductCard;
